/**
 * Mock Payment API Management Page
 * 
 * Displays stored payment requests and allows manual webhook triggering
 */

import { useCallback, useEffect, useState } from 'react';
import { 
  toast,
  DataTable,
  Switch,
} from '@etherisc/ui-kit';
import { PageContent } from '../../components/layout/PageContent';
import { PaymentRequestDetailModal } from '../../components/dev/PaymentRequestDetailModal';
import { mockPaymentApiService } from '../../services/mockPaymentApiService';
import type { StoredPayment } from '../../types/mockPaymentApi';
import type { ActionDefinition } from '../../utils/tableHelpers';
import {
  EyeIcon,
  RefreshCwIcon,
  TrashIcon,
  BanknoteIcon,
} from 'lucide-react';

export function MockPaymentApiPage() {
  // State management
  const [payments, setPayments] = useState<StoredPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interactive, setInteractive] = useState(false);
  const [interactiveLoading, setInteractiveLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Modal state
  const [selectedPayment, setSelectedPayment] = useState<StoredPayment | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const showError = (message: string, description?: string) => toast.error(message, { description });
  const showSuccess = (message: string) => toast.success(message);

  // Fetch stored payments
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mockPaymentApiService.getStoredPayments();
      setPayments(data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch payments';
      setError(errorMessage);
      showError('Failed to fetch payments', errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch interactive mode status
  const fetchInteractiveMode = useCallback(async () => {
    try {
      const mode = await mockPaymentApiService.getInteractiveMode();
      setInteractive(mode);
    } catch (error) {
      console.error('Error fetching interactive mode:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPayments();
    fetchInteractiveMode();
  }, [fetchPayments, fetchInteractiveMode]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchPayments();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchPayments]);

  // Handle interactive mode toggle
  const handleInteractiveToggle = async (checked: boolean) => {
    try {
      setInteractiveLoading(true);
      await mockPaymentApiService.setInteractiveMode(checked);
      setInteractive(checked);
      showSuccess(`Interactive mode ${checked ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error setting interactive mode:', error);
      showError('Failed to update interactive mode');
    } finally {
      setInteractiveLoading(false);
    }
  };

  // Handle view details
  const handleViewDetails = (payment: StoredPayment) => {
    setSelectedPayment(payment);
    setIsViewModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsViewModalOpen(false);
    setSelectedPayment(null);
  };

  // Handle trigger payment
  const handleTriggerPayment = async (payment: StoredPayment) => {
    if (payment.status === 'paid') {
      showError('Payment already processed');
      return;
    }

    try {
      await mockPaymentApiService.triggerPayment(payment.id);
      showSuccess(`Payment webhook triggered for ${payment.id}`);
      // Refresh data
      await fetchPayments();
    } catch (error) {
      console.error('Error triggering payment:', error);
      showError('Failed to trigger payment');
    }
  };

  // Handle clear all payments
  const handleClearAll = async () => {
    if (!confirm('Are you sure you want to clear all stored payments?')) {
      return;
    }

    try {
      await mockPaymentApiService.clearAllPayments();
      showSuccess('All payments cleared');
      setPayments([]);
    } catch (error) {
      console.error('Error clearing payments:', error);
      showError('Failed to clear payments');
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchPayments();
    fetchInteractiveMode();
  };

  // Define row actions
  const actionDefinitions: ActionDefinition<StoredPayment>[] = [
    {
      id: 'view',
      label: 'View Details',
      icon: <EyeIcon size={16} />,
      onClick: handleViewDetails,
    },
    {
      id: 'pay',
      label: 'Pay',
      icon: <BanknoteIcon size={16} />,
      onClick: handleTriggerPayment,
      hidden: (payment) => payment.status === 'paid',
    },
  ];

  // Define table columns
  const columns = [
    {
      id: 'id',
      header: 'Payment ID',
      accessorKey: 'id',
      cell: ({ getValue }: { getValue: () => string }) => (
        <span className="font-mono text-sm">{getValue()}</span>
      ),
    },
    {
      id: 'quoteNumber',
      header: 'Quote #',
      accessorFn: (row: StoredPayment) => row.request.QuoteNumber,
      cell: ({ getValue }: { getValue: () => string }) => <span>{getValue()}</span>,
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorFn: (row: StoredPayment) => row.request.Amount,
      cell: ({ row }: { row: { original: StoredPayment } }) => {
        const payment = row.original;
        return <span>{payment.request.Currency} {payment.request.Amount.toFixed(2)}</span>;
      },
    },
    {
      id: 'customer',
      header: 'Customer',
      accessorFn: (row: StoredPayment) => {
        const customer = row.request.Customer;
        return `${customer.FirstName || ''} ${customer.LastName || ''}`.trim() || 'N/A';
      },
      cell: ({ getValue }: { getValue: () => string }) => <span>{getValue()}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }: { getValue: () => string }) => {
        const status = getValue();
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              status === 'paid'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {status === 'paid' ? 'Paid' : 'Pending'}
          </span>
        );
      },
    },
    {
      id: 'receivedAt',
      header: 'Received At',
      accessorKey: 'receivedAt',
      cell: ({ getValue }: { getValue: () => string }) => {
        const date = new Date(getValue());
        return <span>{date.toLocaleString()}</span>;
      },
    },
    // Actions column
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      enableResizing: false,
      size: 100,
      cell: ({ row }: { row: { original: StoredPayment } }) => {
        const payment = row.original;
        
        // Filter visible actions
        const visibleActions = actionDefinitions.filter(action => !action.hidden?.(payment));
        
        return (
          <div className="flex items-center gap-2">
            {visibleActions.map((action) => (
              <button
                key={action.id}
                onClick={() => action.onClick(payment)}
                disabled={action.disabled?.(payment)}
                className={`p-1 rounded transition-colors ${
                  action.intent === 'danger' 
                    ? action.disabled?.(payment) 
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-red-600 hover:text-red-800'
                    : action.disabled?.(payment)
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-blue-600 hover:text-blue-800'
                }`}
                title={action.label}
              >
                {action.icon}
              </button>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <PageContent 
      title="Mock Payment API"
      description="Monitor and manage payment requests in the Apple Tree mock API for testing"
    >
      {/* Header Controls */}
      <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Interactive Mode Toggle */}
            <div className="flex items-center gap-3">
              <label htmlFor="interactive-mode" className="text-sm font-medium text-gray-700">
                Interactive Mode
              </label>
              <Switch
                id="interactive-mode"
                checked={interactive}
                onCheckedChange={handleInteractiveToggle}
                disabled={interactiveLoading}
              />
              <span className="text-xs text-gray-500">
                {interactive 
                  ? 'Webhooks triggered manually only' 
                  : 'Webhooks auto-trigger on payment receipt'}
              </span>
            </div>

            {/* Auto-refresh Toggle */}
            <div className="flex items-center gap-3 border-l pl-6">
              <label htmlFor="auto-refresh" className="text-sm font-medium text-gray-700">
                Auto-refresh
              </label>
              <Switch
                id="auto-refresh"
                checked={autoRefresh}
                onCheckedChange={setAutoRefresh}
              />
              <span className="text-xs text-gray-500">
                {autoRefresh ? 'Every 5s' : 'Paused'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCwIcon size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleClearAll}
              disabled={loading || payments.length === 0}
              className="inline-flex items-center gap-2 px-3 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
            >
              <TrashIcon size={16} />
              Clear All
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Total Payments:</span>
            <span className="text-sm font-semibold text-gray-900">{payments.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Pending:</span>
            <span className="text-sm font-semibold text-yellow-600">
              {payments.filter(p => p.status === 'pending').length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Paid:</span>
            <span className="text-sm font-semibold text-green-600">
              {payments.filter(p => p.status === 'paid').length}
            </span>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <DataTable
          data={payments}
          columns={columns}
          loading={loading}
          enableSorting={true}
          enableResizing={true}
        />
      </div>

      {/* Detail Modal */}
      <PaymentRequestDetailModal
        payment={selectedPayment}
        isOpen={isViewModalOpen}
        onClose={handleCloseModal}
      />
    </PageContent>
  );
}

